import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import './Page14.css';

const API_BASE = 'https://kidr78home.duckdns.org/api/upload';

function Page14() {
  const [authed, setAuthed] = useState(() => !!sessionStorage.getItem('uploadToken'));
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [results, setResults] = useState([]);
  const fileInputRef = useRef(null);

  const handleAuth = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setAuthLoading(true);
    setAuthError('');
    try {
      await axios.post(`${API_BASE}/auth`, { password });
      sessionStorage.setItem('uploadToken', password);
      setAuthed(true);
    } catch {
      setAuthError('Wrong password');
    } finally {
      setAuthLoading(false);
    }
  };

  const getToken = () => sessionStorage.getItem('uploadToken');

  const addFiles = useCallback((newFiles) => {
    const videoFiles = Array.from(newFiles).filter(f =>
      f.type.startsWith('video/') || /\.(mp4|mov|avi|mkv|webm|m4v|wmv|flv)$/i.test(f.name)
    );
    if (videoFiles.length > 0) {
      setFiles(prev => [...prev, ...videoFiles]);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatSize = (bytes) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const uploadAll = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setResults([]);
    const uploadResults = [];

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await axios.post(`${API_BASE}/video`, formData, {
          headers: { Authorization: getToken(), 'Content-Type': 'multipart/form-data' },
          timeout: 0, // no timeout for large uploads
        });
        uploadResults.push({ name: file.name, success: true, size: res.data.size_mb });
      } catch (err) {
        if (err.response?.status === 401) {
          sessionStorage.removeItem('uploadToken');
          setAuthed(false);
          return;
        }
        const msg = err.response?.data?.error || 'Upload failed';
        uploadResults.push({ name: file.name, success: false, error: msg });
      }
    }

    setResults(uploadResults);
    setFiles([]);
    setUploading(false);
  };

  // Password gate
  if (!authed) {
    return (
      <div className="upload-page">
        <div className="upload-header">
          <h1 className="upload-title">📹 Video Upload</h1>
          <p className="upload-subtitle">Enter password to access</p>
        </div>
        <div className="upload-container">
          <form className="auth-form" onSubmit={handleAuth}>
            <input
              type="password"
              className="auth-input"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
            />
            <button type="submit" className="auth-btn" disabled={authLoading}>
              {authLoading ? '...' : '🔓 Enter'}
            </button>
            {authError && <p className="auth-error">{authError}</p>}
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="upload-page">
      <div className="upload-header">
        <h1 className="upload-title">📹 Video Upload</h1>
        <p className="upload-subtitle">Drop video files here to upload</p>
      </div>

      <div className="upload-container">
        <div
          className={`drop-zone ${dragOver ? 'drag-over' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          aria-label="Drop zone for video files"
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            multiple
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
            style={{ display: 'none' }}
          />
          <span className="drop-icon">{dragOver ? '📥' : '🎬'}</span>
          <span className="drop-text">
            {dragOver ? 'Drop files here' : 'Tap or drag videos here'}
          </span>
          <span className="drop-hint">MP4, MOV, AVI, MKV, WebM — up to 10 GB</span>
        </div>

        {files.length > 0 && (
          <div className="file-list">
            {files.map((file, i) => (
              <div key={`${file.name}-${i}`} className="file-item">
                <span className="file-icon">🎥</span>
                <div className="file-info">
                  <span className="file-name">{file.name}</span>
                  <span className="file-size">{formatSize(file.size)}</span>
                </div>
                <button className="file-remove" onClick={(e) => { e.stopPropagation(); removeFile(i); }} aria-label={`Remove ${file.name}`}>✕</button>
              </div>
            ))}
          </div>
        )}

        {files.length > 0 && !uploading && (
          <button className="upload-btn" onClick={uploadAll}>
            📤 Upload {files.length} file{files.length > 1 ? 's' : ''}
          </button>
        )}

        {uploading && (
          <div className="uploading-state">
            <div className="upload-spinner"></div>
            <span>Uploading...</span>
          </div>
        )}

        {results.length > 0 && (
          <div className="results-list">
            {results.map((r, i) => (
              <div key={i} className={`result-item ${r.success ? 'success' : 'error'}`}>
                <span>{r.success ? '✅' : '❌'}</span>
                <span className="result-name">{r.name}</span>
                <span className="result-detail">
                  {r.success ? `${r.size} MB` : r.error}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Page14;
